import React from 'react';
import { Checkbox } from '@ohif/ui-next';
import classnames from 'classnames';
import type { ImagingWorklistLayout } from '../../utils/roleUtils';
import {
  IMAGING_COLUMN_LABELS,
  type HubSpokeWorklistColumnKey,
  type ImagingColumnKey,
} from '../../constants/imagingWorklistColumns';
import {
  formatAgeDisplay,
  formatAppointmentTypeDisplay,
  formatAssignedHubFacilityDisplay,
  formatSourceFacilityDisplay,
  formatWorklistStudyDateTime,
  formatWorklistReportDateTime,
  priorityUsesUrgentPill,
} from './imagingWorklistTableUtils';
import {
  ImagingWorklistActionCell,
  ImagingWorklistAiResultCell,
  ImagingWorklistModalityCell,
  ImagingWorklistPatientNameCell,
  ImagingWorklistPriorityCell,
  ImagingWorklistReportStatusCell,
} from './imagingWorklistStudyRowCells';

export type ImagingStudyRow = {
  studyInstanceUid: string;
  mrn?: string;
  patientName?: string;
  patientSex?: string;
  patientAge?: string;
  modalities?: string;
  description?: string;
  accession?: string;
  procedureDescription?: string;
  testName?: string;
  worklistPriorityRuleName?: string;
  clinicalHistory?: string;
  appointmentType?: string;
  priority?: string;
  reportStatus?: string;
  assignedName?: string;
  assignedHubFacilityId?: string;
  assignedHubFacilityName?: string;
  sourceFacilityId?: string;
  sourceFacilityName?: string;
  isCritical?: boolean;
  criticalReason?: string;
  date?: string;
  time?: string;
  reportDateTime?: string;
  aiStatus?: string;
  aiClassification?: string;
  aiModelVersion?: string;
};

export type ImagingWorklistTableProps = {
  layout: ImagingWorklistLayout;
  studies: ImagingStudyRow[];
  pageOffset: number;
  showCheckboxes: boolean;
  columnVisibility: Record<ImagingColumnKey, boolean>;
  checkedStudies: Set<string>;
  onToggleStudyCheck: (studyInstanceUid: string, checked: boolean) => void;
  /** Toggle every row on the current page (header checkbox; Figma order: checkbox column first). */
  onToggleAllPageStudies?: (checked: boolean) => void;
  getStudyLaunchHref: (study: ImagingStudyRow) => string | null;
  onRadiologistOpenStudy?: (study: ImagingStudyRow) => void;
  hubFacilityNameById?: Record<string, string>;
  hubSpokeColumnKey?: HubSpokeWorklistColumnKey | null;
};

type ImagingWorklistStudyRowProps = {
  study: ImagingStudyRow;
  rowKey: number;
  layout: ImagingWorklistLayout;
  showCheckboxes: boolean;
  columnVisibility: Record<ImagingColumnKey, boolean>;
  checkedStudies: Set<string>;
  onToggleStudyCheck: (studyInstanceUid: string, checked: boolean) => void;
  getStudyLaunchHref: (study: ImagingStudyRow) => string | null;
  onRadiologistOpenStudy?: (study: ImagingStudyRow) => void;
  hubFacilityNameById?: Record<string, string>;
  showAssigned: boolean;
  showAssignedHubFacility: boolean;
  showSourceFacility: boolean;
};

function ImagingWorklistStudyRow({
  study,
  rowKey,
  layout,
  showCheckboxes,
  columnVisibility,
  checkedStudies,
  onToggleStudyCheck,
  getStudyLaunchHref,
  onRadiologistOpenStudy,
  hubFacilityNameById,
  showAssigned,
  showAssignedHubFacility,
  showSourceFacility,
}: Readonly<ImagingWorklistStudyRowProps>) {
  const launchHref = getStudyLaunchHref(study);
  const priorityRaw = study.priority?.trim() || '';
  const priorityIsUrgentPill = priorityUsesUrgentPill(priorityRaw);
  const worklistBoostRule = study.worklistPriorityRuleName?.trim() || '';
  const isCritical = study.isCritical === true;

  const rowBg = classnames('border-b border-slate-100', {
    'imaging-worklist-table__row--critical': isCritical,
    'bg-white': !isCritical,
  });

  const openStudyFromRow =
    layout === 'radiologist' && launchHref ? () => onRadiologistOpenStudy?.(study) : undefined;

  return (
    <tr
      className={rowBg}
      onClick={openStudyFromRow}
      style={openStudyFromRow ? { cursor: 'pointer' } : undefined}
    >
      {showCheckboxes && (
        <td
          className="w-10 px-2 py-3 text-center align-middle"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-center">
            <Checkbox
              checked={checkedStudies.has(study.studyInstanceUid)}
              onCheckedChange={c => onToggleStudyCheck(study.studyInstanceUid, c === true)}
              className="h-4 w-4 border-slate-400 data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600"
            />
          </div>
        </td>
      )}
      <td className="whitespace-nowrap px-3 py-3 text-slate-600">{rowKey}</td>
      {columnVisibility.uhid !== false && (
        <td
          className="imaging-worklist-uhid"
          title={study.mrn?.trim() || ''}
        >
          <div className="imaging-worklist-uhid__inner">
            <span className="imaging-worklist-uhid__text">{study.mrn || '—'}</span>
          </div>
        </td>
      )}
      {columnVisibility.patientName !== false && (
        <ImagingWorklistPatientNameCell
          study={study}
          isCritical={isCritical}
        />
      )}
      {columnVisibility.age !== false && (
        <>
          <td className="whitespace-nowrap px-3 py-3">{formatAgeDisplay(study.patientAge)}</td>
          <td className="whitespace-nowrap px-3 py-3">{study.patientSex?.trim() || '—'}</td>
        </>
      )}
      {columnVisibility.modality !== false && (
        <ImagingWorklistModalityCell modalities={study.modalities} />
      )}
      {columnVisibility.studyDescription !== false && (
        <td
          className="imaging-worklist-study-description"
          title={study.description?.trim() || undefined}
        >
          <div className="imaging-worklist-study-description__inner">
            <div className="imaging-worklist-study-description__title">
              {study.description?.trim() || '—'}
            </div>
          </div>
        </td>
      )}
      {columnVisibility.studyDateTime !== false && (
        <td className="whitespace-nowrap px-3 py-3">
          {formatWorklistStudyDateTime(study.date, study.time)}
        </td>
      )}
      {columnVisibility.tests !== false && (
        <td
          className="imaging-worklist-tests"
          title={
            study.testName?.trim() || study.procedureDescription?.trim() || study.description || ''
          }
        >
          <div className="imaging-worklist-tests__inner">
            <span className="imaging-worklist-tests__text">
              {study.testName?.trim() || study.procedureDescription?.trim() || '—'}
            </span>
          </div>
        </td>
      )}
      {columnVisibility.clinicalHistory !== false && (
        <td
          className="imaging-worklist-clinical-history"
          title={study.clinicalHistory?.trim() || ''}
        >
          <div className="imaging-worklist-clinical-history__inner">
            <span className="imaging-worklist-clinical-history__text">
              {study.clinicalHistory?.trim() || '—'}
            </span>
          </div>
        </td>
      )}
      {columnVisibility.appointmentType !== false && (
        <td
          className="whitespace-nowrap px-3 py-3"
          title={formatAppointmentTypeDisplay(study.appointmentType) || undefined}
        >
          {formatAppointmentTypeDisplay(study.appointmentType) || '—'}
        </td>
      )}
      {columnVisibility.priority !== false && (
        <ImagingWorklistPriorityCell
          priorityRaw={priorityRaw}
          priorityIsUrgentPill={priorityIsUrgentPill}
          worklistBoostRule={worklistBoostRule}
          studyPriority={study.priority}
        />
      )}
      {columnVisibility.reportStatus !== false && (
        <ImagingWorklistReportStatusCell reportStatus={study.reportStatus} />
      )}
      {columnVisibility.reportDateTime !== false && (
        <td className="whitespace-nowrap px-3 py-3">
          {formatWorklistReportDateTime(study.reportDateTime)}
        </td>
      )}
      {columnVisibility.aiResult !== false && (
        <ImagingWorklistAiResultCell
          aiStatus={study.aiStatus}
          aiClassification={study.aiClassification}
          aiModelVersion={study.aiModelVersion}
        />
      )}
      {showAssigned && (
        <td className="max-w-[140px] truncate px-3 py-3">
          {study.assignedName?.trim() || 'Unassigned'}
        </td>
      )}
      {showAssignedHubFacility && (
        <td
          className="max-w-[160px] truncate px-3 py-3"
          title={formatAssignedHubFacilityDisplay(
            study.assignedHubFacilityId,
            study.assignedHubFacilityName,
            hubFacilityNameById
          )}
        >
          {formatAssignedHubFacilityDisplay(
            study.assignedHubFacilityId,
            study.assignedHubFacilityName,
            hubFacilityNameById
          )}
        </td>
      )}
      {showSourceFacility && (
        <td
          className="max-w-[160px] truncate px-3 py-3"
          title={formatSourceFacilityDisplay(study.sourceFacilityName)}
        >
          {formatSourceFacilityDisplay(study.sourceFacilityName)}
        </td>
      )}
      {columnVisibility.action !== false && <ImagingWorklistActionCell launchHref={launchHref} />}
    </tr>
  );
}

export default function ImagingWorklistTable({
  layout,
  studies,
  pageOffset,
  showCheckboxes,
  columnVisibility,
  checkedStudies,
  onToggleStudyCheck,
  onToggleAllPageStudies,
  getStudyLaunchHref,
  onRadiologistOpenStudy,
  hubFacilityNameById,
  hubSpokeColumnKey = null,
}: Readonly<ImagingWorklistTableProps>) {
  const showAssigned = layout === 'adminTech' && columnVisibility.assigned !== false;
  const showAssignedHubFacility =
    layout === 'adminTech' &&
    hubSpokeColumnKey === 'assignedHubFacility' &&
    columnVisibility.assignedHubFacility !== false;
  const showSourceFacility =
    layout === 'adminTech' &&
    hubSpokeColumnKey === 'sourceFacility' &&
    columnVisibility.sourceFacility !== false;

  const pageUids = studies.map(studyRow => studyRow.studyInstanceUid);
  const allOnPageSelected = pageUids.length > 0 && pageUids.every(uid => checkedStudies.has(uid));
  const someOnPageSelected = pageUids.some(uid => checkedStudies.has(uid));
  const headerCheckState: boolean | 'indeterminate' = allOnPageSelected
    ? true
    : someOnPageSelected
      ? 'indeterminate'
      : false;

  return (
    <div className="imaging-worklist-table-outer bg-white">
      <table
        className="imaging-worklist-table w-full text-left"
        style={{
          tableLayout: 'auto',
          minWidth: 1100,
        }}
      >
        <thead>
          <tr className="imaging-worklist-table__header-row border-b border-slate-200 bg-slate-50">
            {showCheckboxes && (
              <th
                className="w-10 px-2 py-3 text-center align-middle"
                aria-label="Select all on this page"
              >
                {onToggleAllPageStudies ? (
                  <div
                    className="flex justify-center"
                    onClick={e => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={headerCheckState}
                      onCheckedChange={c => onToggleAllPageStudies(c === true)}
                      className="h-4 w-4 border-slate-400 data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600"
                    />
                  </div>
                ) : null}
              </th>
            )}
            <th className="whitespace-nowrap px-3 py-3">S.No</th>
            {columnVisibility.uhid !== false && (
              <th className="imaging-worklist-uhid-col whitespace-nowrap py-3">
                {IMAGING_COLUMN_LABELS.uhid}
              </th>
            )}
            {columnVisibility.patientName !== false && (
              <th className="whitespace-nowrap px-3 py-3">{IMAGING_COLUMN_LABELS.patientName}</th>
            )}
            {columnVisibility.age !== false && (
              <>
                <th className="whitespace-nowrap px-3 py-3">Age</th>
                <th className="whitespace-nowrap px-3 py-3">Gender</th>
              </>
            )}
            {columnVisibility.modality !== false && (
              <th className="imaging-worklist-modality-col whitespace-nowrap px-3 py-3">
                {IMAGING_COLUMN_LABELS.modality}
              </th>
            )}
            {columnVisibility.studyDescription !== false && (
              <th className="imaging-worklist-study-description-col py-3">
                {IMAGING_COLUMN_LABELS.studyDescription}
              </th>
            )}
            {columnVisibility.studyDateTime !== false && (
              <th className="whitespace-nowrap px-3 py-3">{IMAGING_COLUMN_LABELS.studyDateTime}</th>
            )}
            {columnVisibility.tests !== false && (
              <th className="imaging-worklist-tests-col py-3">{IMAGING_COLUMN_LABELS.tests}</th>
            )}
            {columnVisibility.clinicalHistory !== false && (
              <th className="imaging-worklist-clinical-history-col py-3">
                {IMAGING_COLUMN_LABELS.clinicalHistory}
              </th>
            )}
            {columnVisibility.appointmentType !== false && (
              <th className="whitespace-nowrap px-3 py-3">
                {IMAGING_COLUMN_LABELS.appointmentType}
              </th>
            )}
            {columnVisibility.priority !== false && (
              <th className="whitespace-nowrap px-3 py-3 text-center align-middle">
                {IMAGING_COLUMN_LABELS.priority}
              </th>
            )}
            {columnVisibility.reportStatus !== false && (
              <th className="whitespace-nowrap px-3 py-3 text-center align-middle">
                {IMAGING_COLUMN_LABELS.reportStatus}
              </th>
            )}
            {columnVisibility.reportDateTime !== false && (
              <th className="whitespace-nowrap px-3 py-3">
                {IMAGING_COLUMN_LABELS.reportDateTime}
              </th>
            )}
            {columnVisibility.aiResult !== false && (
              <th className="whitespace-nowrap px-3 py-3 text-center align-middle">
                {IMAGING_COLUMN_LABELS.aiResult}
              </th>
            )}
            {showAssigned && (
              <th className="whitespace-nowrap px-3 py-3">{IMAGING_COLUMN_LABELS.assigned}</th>
            )}
            {showAssignedHubFacility && (
              <th className="whitespace-nowrap px-3 py-3">
                {IMAGING_COLUMN_LABELS.assignedHubFacility}
              </th>
            )}
            {showSourceFacility && (
              <th className="whitespace-nowrap px-3 py-3">
                {IMAGING_COLUMN_LABELS.sourceFacility}
              </th>
            )}
            {columnVisibility.action !== false && (
              <th className="imaging-worklist-table__action-col">
                <div className="imaging-worklist-action-col__inner">
                  {IMAGING_COLUMN_LABELS.action}
                </div>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {studies.map((study, i) => (
            <ImagingWorklistStudyRow
              key={study.studyInstanceUid}
              study={study}
              rowKey={pageOffset + i + 1}
              layout={layout}
              showCheckboxes={showCheckboxes}
              columnVisibility={columnVisibility}
              checkedStudies={checkedStudies}
              onToggleStudyCheck={onToggleStudyCheck}
              getStudyLaunchHref={getStudyLaunchHref}
              onRadiologistOpenStudy={onRadiologistOpenStudy}
              hubFacilityNameById={hubFacilityNameById}
              showAssigned={showAssigned}
              showAssignedHubFacility={showAssignedHubFacility}
              showSourceFacility={showSourceFacility}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
